import { axiosClientEmailMarketing } from '../../axios/api';
import { getApiBaseUrl } from '../config/api';

export { getApiBaseUrl };
export const API_BASE_URL = getApiBaseUrl();

export const request = async (endpoint, options = {}) => {
  // Strip leading '/api/' or '/api' or '/' so endpoint resolves properly against baseURL (which includes /api)
  let url = endpoint;
  if (url.startsWith('/api/')) {
    url = url.substring(5);
  } else if (url.startsWith('/api')) {
    url = url.substring(4);
  } else if (url.startsWith('/')) {
    url = url.substring(1);
  }

  const config = {
    method: options.method || 'GET',
    url,
    headers: options.headers,
  };

  if (options.body) {
    config.data = options.body;
  }

  try {
    const response = await axiosClientEmailMarketing(config);
    return response.data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || error.response.data.error || `HTTP error! status: ${error.response.status}`);
    }
    throw error;
  }
};

// Centralized event deduplicator to prevent duplicate items from Lambda/Apache retries or database insertion overlap.
export const deduplicateEvents = (events) => {
  if (!Array.isArray(events)) return [];
  const seenIds = new Set();
  const seenUniqueKeys = new Set();
  return events.filter(e => {
    if (!e) return false;
    
    // 1. Deduplicate by unique database ID if available
    const id = e._id || e.id;
    if (id) {
      if (seenIds.has(id)) return false;
      seenIds.add(id);
    }
    
    // 2. Deduplicate by unique key: messageId (and URL for clicks)
    const msgId = e.messageId;
    if (msgId) {
      const urlKey = e.url || e.originalUrl || '';
      const uniqueKey = urlKey ? `${msgId}_${urlKey}` : msgId;
      if (seenUniqueKeys.has(uniqueKey)) return false;
      seenUniqueKeys.add(uniqueKey);
    } else {
      // Fallback: If no messageId, deduplicate by composite fields (sender, subject/type, and approximate timestamp)
      const recipient = e.recipientEmail || (e.contactId && typeof e.contactId === 'object' ? e.contactId.email : '') || '';
      const typeKey = e.url || e.originalUrl || e.replySubject || e.bounceType || e.blockType || '';
      const fallbackKey = `${recipient}_${typeKey}`;
      if (seenUniqueKeys.has(fallbackKey)) return false;
      seenUniqueKeys.add(fallbackKey);
    }
    return true;
  });
};

export const deduplicatePayloadEvents = (payload) => {
  if (!payload) return payload;
  const rawEvents = payload.events;
  if (Array.isArray(rawEvents)) {
    const uniqueEvents = deduplicateEvents(rawEvents);
    const diff = rawEvents.length - uniqueEvents.length;
    payload.events = uniqueEvents;
    if (diff > 0 && payload.pagination && typeof payload.pagination.totalItems === 'number') {
      payload.pagination.totalItems = Math.max(0, payload.pagination.totalItems - diff);
    }
    if (diff > 0 && typeof payload.total === 'number') {
      payload.total = Math.max(0, payload.total - diff);
    }
    if (diff > 0 && typeof payload.totalCount === 'number') {
      payload.totalCount = Math.max(0, payload.totalCount - diff);
    }
    if (diff > 0 && payload.analytics) {
      if (typeof payload.analytics.replies === 'number') {
        payload.analytics.replies = Math.max(0, payload.analytics.replies - diff);
      }
      if (typeof payload.analytics.uniqueReplies === 'number') {
        payload.analytics.uniqueReplies = Math.max(0, payload.analytics.uniqueReplies - diff);
      }
      if (typeof payload.analytics.unreadReplies === 'number') {
        // Adjust unreadReplies based on how many unread events were removed
        const rawUnreadRemoved = rawEvents.filter((e, idx) => {
          return !uniqueEvents.includes(e) && e.isRead === false && !e.isOutgoing;
        }).length;
        payload.analytics.unreadReplies = Math.max(0, payload.analytics.unreadReplies - rawUnreadRemoved);
      }
    }
  }
  return payload;
};

export const deduplicateCampaigns = (campaigns) => {
  if (!Array.isArray(campaigns)) return [];
  const seen = new Set();
  return campaigns.filter(c => {
    const rawId = c?._id || c?.id;
    const id = rawId ? String(rawId) : '';
    if (id && seen.has(id)) return false;
    if (id) seen.add(id);
    return true;
  });
};

export const deduplicateSubscribers = (subscribers) => {
  if (!Array.isArray(subscribers)) return [];
  const seen = new Set();
  return subscribers.filter(s => {
    const id = s._id || s.id;
    if (id && seen.has(id)) return false;
    if (id) seen.add(id);
    return true;
  });
};

export const deduplicateTemplates = (templates) => {
  if (!Array.isArray(templates)) return [];
  const seen = new Set();
  return templates.filter(t => {
    const id = t._id || t.id;
    if (id && seen.has(id)) return false;
    if (id) seen.add(id);
    return true;
  });
};

// --- Contact API Calls ---
export const fetchContacts = async (page = 1, limit = 20) => {
  const response = await request(`/api/contacts?page=${page}&limit=${limit}`);
  return response.data;
};

export const createContact = async (contactData) => {
  const response = await request('/api/contacts', {
    method: 'POST',
    body: JSON.stringify(contactData),
  });
  return response.data;
};

export const updateContact = async (id, contactData) => {
  const response = await request(`/api/contacts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(contactData),
  });
  return response.data;
};

export const deleteContact = async (id) => {
  // DELETE requests might not return a body, so we just await the request
  await request(`/api/contacts/${id}`, {
    method: 'DELETE',
  });
};

// --- Subscriber API Calls ---
export const subscribeEmail = async (email) => {
  return request('/api/subscribers/subscribe', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
};

export const fetchSubscribers = async (page = 1, limit = 20, search = '', filters = {}) => {
  const qs = new URLSearchParams({ page, limit, search });
  // Column filters: ?company=A,B&subscribed=Yes. Empty ones are dropped so the URL stays
  // readable and the server never sees a parameter that means "no filter".
  Object.entries(filters || {}).forEach(([k, v]) => { if (v) qs.set(k, v); });
  const params = qs.toString();
  const response = await request(`/api/subscribers?${params}`);
  if (response && response.data && Array.isArray(response.data.subscribers)) {
    response.data.subscribers = deduplicateSubscribers(response.data.subscribers);
  }
  return response.data; // { subscribers, pagination }
};

export const createSubscriber = async (data) => {
  const response = await request('/api/subscribers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.data.subscriber;
};

export const updateSubscriber = async (id, data) => {
  const response = await request(`/api/subscribers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.data.subscriber;
};

export const deleteSubscriber = async (id, targetTable) => {
  await request(`/api/subscribers/${id}?targetTable=${encodeURIComponent(targetTable)}`, {
    method: 'DELETE',
  });
};

// --- Company options for Add Contact form ---
export const fetchCompanyOptions = async ({ targetTable, q = '' }) => {
  const paramsObj = { targetTable };
  if (q && q.length) paramsObj.q = q;
  const params = new URLSearchParams(paramsObj).toString();
  const response = await request(`/api/subscribers/companies?${params}`);
  return response.data; // [{ id, name }]
};

// --- Company locations (billing + shipping) filtered by company_name ---
export const fetchCompanyLocations = async ({ targetTable, companyName }) => {
  const params = new URLSearchParams({ targetTable, companyName }).toString();
  const response = await request(`/api/subscribers/locations?${params}`);
  return response.data; // [{ line1, line2, line3, city, state, country, source }]
};

// --- Templates API ---
export const fetchTemplatesApi = async () => {
  const response = await request('/api/templates');
  if (response && Array.isArray(response.data)) {
    response.data = deduplicateTemplates(response.data);
  }
  return response.data; // array
};

export const fetchTemplateByIdApi = async (id) => {
  const response = await request(`/api/templates/${encodeURIComponent(id)}`);
  return response.data;
};

export const createTemplateApi = async (template) => {
  const response = await request('/api/templates', {
    method: 'POST',
    body: JSON.stringify(template),
  });
  return response.data;
};

// Partial update (PATCH) for small changes like favorite toggle
export const patchTemplateApi = async (id, patch) => {
  const response = await request(`/api/templates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return response.data;
};

export const deleteTemplateApi = async (id) => {
  await request(`/api/templates/${id}`, { method: 'DELETE' });
};

// --- AI helpers for the template editor ---
// Returns up to three draft email bodies (HTML) written from the subject line.
// `selection` is the optional { product, material } picked from the lists.
export const suggestEmailBodiesApi = async (subject, selection = {}) => {
  const response = await request('/api/ai/suggest-bodies', {
    method: 'POST',
    body: JSON.stringify({ subject, product: selection.product, material: selection.material }),
  });
  return response.data.bodies;
};

// Writes complete emails (subject + body) from a short request such as "titanium pipe".
// Returns up to three { subject, body } options.
export const generateEmailApi = async (requestText, selection = {}) => {
  const response = await request('/api/ai/generate-email', {
    method: 'POST',
    body: JSON.stringify({ request: requestText, product: selection.product, material: selection.material }),
  });
  return response.data.emails;
};

// Products with the materials we supply for each, read from the product database.
export const fetchCatalogOptionsApi = async () => {
  const response = await request('/api/ai/catalog-options');
  return response.data.products;
};

// --- Analytics API Calls ---
export const fetchDashboardSummary = async (params = {}) => {
  const cleanParams = {};
  if (params.dateFrom) cleanParams.dateFrom = params.dateFrom;
  if (params.dateTo) cleanParams.dateTo = params.dateTo;
  const query = new URLSearchParams(cleanParams).toString();
  const endpoint = query ? `/api/analytics/dashboard?${query}` : '/api/analytics/dashboard';
  const response = await request(endpoint);
  // Backend returns { success, data: { summary: {...}, overview: {...}, rates: {...} } }
  // Extract the summary object so stat cards can access .campaigns, .contacts, etc. directly
  return response?.data?.summary ?? response?.data ?? response;
};

export const fetchTimeseries = async (days) => {
  const query = days ? `?days=${days}` : '';
  const response = await request(`/api/analytics/timeseries${query}`);
  // Backend returns { success, data: [...points], granularity }
  // Preserve granularity alongside the points array
  const points = response?.data ?? [];
  const granularity = response?.granularity;
  return { points, granularity };
};

// --- Campaign API Calls ---

/**
 * Fetches a list of campaigns, with optional query parameters for pagination and filtering.
 * @param {object} params - Query parameters (e.g., { page: 1, limit: 10 }).
 */
export const fetchCampaigns = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  try {
    const response = await request(`/api/campaigns?${query}`);
    console.log('Raw campaign API response:', response);
    
    // The backend returns: { success: true, data: { campaigns: [...], pagination: {...} } }
    if (response && response.success && response.data && Array.isArray(response.data.campaigns)) {
      return { campaigns: deduplicateCampaigns(response.data.campaigns), pagination: response.data.pagination };
    }
    
    // Handle legacy format where data is directly the array
    if (response && response.data && Array.isArray(response.data)) {
      return { campaigns: deduplicateCampaigns(response.data) };
    }
    
    // If response is directly an array
    if (Array.isArray(response)) {
      return { campaigns: deduplicateCampaigns(response) };
    }
    
    // If response has campaigns property at root level
    if (response && Array.isArray(response.campaigns)) {
      return { campaigns: deduplicateCampaigns(response.campaigns) };
    }
    
    console.warn('Unexpected campaign API response format:', response);
    return { campaigns: [] }; // Return empty array as fallback
  } catch (error) {
    console.error('Failed to fetch campaigns from API:', error);
    return { campaigns: [] }; // Return empty array on error
  }
};

/**
 * Fetches a single campaign by ID.
 * @param {string} id - The campaign ID.
 */
export const fetchCampaignById = async (id) => {
  try {
    const response = await request(`/api/campaigns/${id}`);
    return response?.data?.campaign || response?.campaign || response?.data || response;
  } catch (error) {
    console.error(`Failed to fetch campaign by ID ${id}:`, error);
    return null;
  }
};

/**
 * Creates a new campaign.
 * @param {object} campaignData - The data for the new campaign.
 */
export const createCampaign = async (campaignData) => {
  const response = await request('/api/campaigns', {
    method: 'POST',
    body: JSON.stringify(campaignData),
  });
  // Backend returns { success: true, data: { campaign }, message }
  return response.data || response;
};

/**
 * Updates an existing campaign.
 * @param {string} id - The ID of the campaign to update.
 * @param {object} campaignData - The updated data for the campaign.
 */
// --- Campaign audience entries ---
// Each call touches ONE entry; the campaign's other audiences are left alone, which is what
// keeps a second set of matching rules from being merged into the first.
export const addCampaignAudience = async (campaignId, audience) => {
  const response = await request(`/api/campaigns/${campaignId}/audiences`, {
    method: 'POST',
    body: JSON.stringify(audience),
  });
  return response.data;
};

export const updateCampaignAudience = async (campaignId, audienceId, audience) => {
  const response = await request(`/api/campaigns/${campaignId}/audiences/${audienceId}`, {
    method: 'PUT',
    body: JSON.stringify(audience),
  });
  return response.data;
};

export const deleteCampaignAudience = async (campaignId, audienceId) => {
  const response = await request(`/api/campaigns/${campaignId}/audiences/${audienceId}`, {
    method: 'DELETE',
  });
  return response.data;
};

export const updateCampaign = async (id, campaignData) => {
  const response = await request(`/api/campaigns/${id}`, {
    method: 'PUT',
    body: JSON.stringify(campaignData),
  });
  return response.data;
};

export const patchCampaign = async (id, campaignData) => {
  const response = await request(`/api/campaigns/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(campaignData),
  });
  return response.data;
};

/**
 * Deletes a campaign.
 * @param {string} id - The ID of the campaign to delete.
 */
export const deleteCampaignApi = async (id) => {
  await request(`/api/campaigns/${id}`, {
    method: 'DELETE',
  });
};


// Send test email for campaign
export const sendTestEmail = async (campaignId, testEmails) => {
  const response = await request(`/api/campaigns/${campaignId}/test`, {
    method: 'POST',
    body: JSON.stringify({ testEmails }),
  });
  return response.data;
};

// Pause/resume campaign
export const pauseCampaign = async (campaignId, action) => {
  const response = await request(`/api/campaigns/${campaignId}/pause`, {
    method: 'POST',
    body: JSON.stringify({ action }),
  });
  return response.data;
};

/**
 * Runs one action over many campaigns in a single request.
 *
 * The endpoint is deliberately partial: a campaign that cannot take the action is reported
 * in `results` rather than failing the whole batch, so twenty selected rows never hinge on
 * the one that is mid-send.
 *
 * @param {'pause'|'resume'|'delete'|'edit'} action
 * @param {string[]} ids - campaign ids to act on
 * @param {object} [changes] - field changes, for the 'edit' action only
 * @returns {Promise<{action: string, results: Array<{id: string, name?: string, success: boolean, message: string}>, summary: {total: number, succeeded: number, failed: number}}>}
 */
export const bulkCampaigns = async (action, ids, changes) => {
  const response = await request('/api/campaigns/bulk', {
    method: 'POST',
    // `changes` only rides along for the edit action; the others take no input.
    body: JSON.stringify(changes ? { action, ids, changes } : { action, ids }),
  });
  const data = response?.data || {};
  return {
    action: data.action || action,
    results: Array.isArray(data.results) ? data.results : [],
    summary: data.summary || { total: 0, succeeded: 0, failed: 0 },
    message: response?.message || '',
  };
};

// Get campaign statistics
export const getCampaignStats = async (campaignId) => {
  const response = await request(`/api/campaigns/${campaignId}/stats`);
  return response.data;
};

// ---------------------------------------------------------------------------
// Sub-campaigns (waves inside a campaign)
// ---------------------------------------------------------------------------

const jsonBody = (payload) => JSON.stringify(payload);

/** List the waves under a campaign. Returns { subCampaigns, summary, pagination }. */
export const fetchSubCampaigns = async (campaignId, params = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  const response = await request(`/api/campaigns/${campaignId}/sub-campaigns${query ? `?${query}` : ''}`);
  return response?.data || { subCampaigns: [], summary: null, pagination: null };
};

export const fetchSubCampaignById = async (id) => {
  const response = await request(`/api/sub-campaigns/${id}`);
  return response?.data?.subCampaign || null;
};

export const createSubCampaignApi = async (campaignId, payload) => {
  const response = await request(`/api/campaigns/${campaignId}/sub-campaigns`, {
    method: 'POST',
    body: jsonBody(payload),
  });
  return response?.data?.subCampaign || response?.data;
};

export const updateSubCampaignApi = async (id, payload) => {
  const response = await request(`/api/sub-campaigns/${id}`, {
    method: 'PATCH',
    body: jsonBody(payload),
  });
  return response?.data?.subCampaign || response?.data;
};

export const deleteSubCampaignApi = async (id) => {
  await request(`/api/sub-campaigns/${id}`, { method: 'DELETE' });
};

export const duplicateSubCampaignApi = async (id, name) => {
  const response = await request(`/api/sub-campaigns/${id}/duplicate`, {
    method: 'POST',
    body: jsonBody(name ? { name } : {}),
  });
  return response?.data?.subCampaign || response?.data;
};

/** action: 'pause' | 'resume' */
export const toggleSubCampaignApi = async (id, action) => {
  const response = await request(`/api/sub-campaigns/${id}/pause`, {
    method: 'POST',
    body: jsonBody({ action }),
  });
  return response?.data?.subCampaign || response?.data;
};

// Close a wave's open failure: 'retry' reschedules it, 'abandon' stops it (note required).
export const resolveSubCampaignFailureApi = async (id, action, note = '') => {
  const response = await request(`/api/sub-campaigns/${id}/failure`, {
    method: 'POST',
    body: jsonBody({ action, note }),
  });
  return response?.data?.subCampaign || response?.data;
};

export const runSubCampaignApi = async (id) => {
  const response = await request(`/api/sub-campaigns/${id}/run`, { method: 'POST' });
  return response?.data?.subCampaign || response?.data;
};

export const updateSubCampaignScheduleApi = async (id, scheduling) => {
  const response = await request(`/api/sub-campaigns/${id}/schedule`, {
    method: 'PATCH',
    body: jsonBody({ scheduling }),
  });
  return response?.data?.subCampaign || response?.data;
};

/** mode: 'replace' | 'append' */
export const assignSubCampaignTemplatesApi = async (id, steps, mode = 'replace') => {
  const response = await request(`/api/sub-campaigns/${id}/templates`, {
    method: 'PUT',
    body: jsonBody({ steps, mode }),
  });
  return response?.data?.subCampaign || response?.data;
};

/** mode: 'replace' | 'append' | 'remove' (remove takes { emails }) */
export const assignSubCampaignRecipientsApi = async (id, payload) => {
  const response = await request(`/api/sub-campaigns/${id}/recipients`, {
    method: 'PUT',
    body: jsonBody(payload),
  });
  return response?.data?.subCampaign || response?.data;
};

export const fetchSubCampaignRecipients = async (id, params = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  const response = await request(`/api/sub-campaigns/${id}/recipients${query ? `?${query}` : ''}`);
  return response?.data || { recipients: [], pagination: null };
};

export const fetchSubCampaignStats = async (id) => {
  const response = await request(`/api/sub-campaigns/${id}/stats`);
  return response?.data || null;
};

/** action: 'pause' | 'resume' | 'delete' | 'status' */
export const bulkSubCampaignApi = async (campaignId, ids, action, status) => {
  const response = await request(`/api/campaigns/${campaignId}/sub-campaigns/bulk`, {
    method: 'POST',
    body: jsonBody({ ids, action, status }),
  });
  return response?.data || { affected: 0 };
};

/**
 * The whole assignment map for a campaign in one request: which waves each recipient
 * is in, and which waves use each template. Powers the "Sub-campaigns" columns on the
 * Recipients and Content tabs without a request per row.
 */
export const fetchSubCampaignAssignments = async (campaignId) => {
  const response = await request(`/api/campaigns/${campaignId}/sub-campaigns/assignments`);
  return response?.data || { subCampaigns: [], recipients: {}, templates: {} };
};

/** mode: 'append' | 'remove' */
export const assignRecipientsToSubCampaignsApi = async (campaignId, subCampaignIds, emails, mode = 'append') => {
  const response = await request(`/api/campaigns/${campaignId}/sub-campaigns/assign-recipients`, {
    method: 'POST',
    body: jsonBody({ subCampaignIds, emails, mode }),
  });
  return response?.data || { results: [] };
};

/** mode: 'append' | 'replace' | 'remove' */
export const assignTemplatesToSubCampaignsApi = async (campaignId, subCampaignIds, steps, mode = 'append') => {
  const response = await request(`/api/campaigns/${campaignId}/sub-campaigns/assign-templates`, {
    method: 'POST',
    body: jsonBody({ subCampaignIds, steps, mode }),
  });
  return response?.data || { results: [] };
};

/** Ask the server for the next few fire times of a schedule that has not been saved yet. */
export const previewSubCampaignSchedule = async (campaignId, scheduling) => {
  const query = new URLSearchParams({ scheduling: JSON.stringify(scheduling) }).toString();
  const response = await request(`/api/campaigns/${campaignId}/sub-campaigns/preview-schedule?${query}`);
  return response?.data?.occurrences || [];
};

// --- Tracking analytics for a campaign ---
export const getCampaignTracking = async (campaignId, type = 'opens', limit = 500, startDate, endDate) => {
  const paramsObj = { campaignId, type, limit: String(limit) };
  if (startDate) paramsObj.startDate = startDate;
  if (endDate) paramsObj.endDate = endDate;
  const params = new URLSearchParams(paramsObj).toString();
  const response = await request(`/api/analytics/tracking?${params}`);
  return deduplicatePayloadEvents(response.data); // { type, events, total, analytics: { ... } }
};

// Campaign-level paginated tracking
export const getCampaignTrackingPaginated = async (campaignId, eventType, page = 1, limit = 5, startDate, endDate) => {
  const paramsObj = { page: String(page), limit: String(limit) };
  if (startDate) paramsObj.startDate = startDate;
  if (endDate) paramsObj.endDate = endDate;
  const qs = new URLSearchParams(paramsObj).toString();
  const response = await request(`/api/tracking/campaigns/${encodeURIComponent(campaignId)}/${eventType}?${qs}`);
  return deduplicatePayloadEvents(response.data); // { events, pagination }
};

// Template-level paginated tracking
export const getTemplateTrackingPaginated = async (templateId, eventType, page = 1, limit = 5, startDate, endDate) => {
  const paramsObj = { page: String(page), limit: String(limit) };
  if (startDate) paramsObj.startDate = startDate;
  if (endDate) paramsObj.endDate = endDate;
  const qs = new URLSearchParams(paramsObj).toString();
  const response = await request(`/api/tracking/templates/${encodeURIComponent(templateId)}/${eventType}?${qs}`);
  return deduplicatePayloadEvents(response.data); // { events, pagination }
};


// Contact-level tracking events (across campaigns)
export const getContactTracking = async (contactId, type = 'opens', page = 1, limit = 500, campaignId, startDate, endDate) => {
  const paramsObj = { page: String(page), limit: String(limit) };
  if (campaignId) paramsObj.campaignId = String(campaignId);
  if (startDate) paramsObj.startDate = startDate;
  if (endDate) paramsObj.endDate = endDate;
  const qs = new URLSearchParams(paramsObj).toString();
  const response = await request(`/api/tracking/contacts/${encodeURIComponent(contactId)}/${type}?${qs}`);
  return deduplicatePayloadEvents(response.data); // { events, pagination }
};

export const getContactsBatchAnalytics = async (emails = []) => {
  const response = await request('/api/tracking/contacts/batch-analytics', {
    method: 'POST',
    body: JSON.stringify({ emails })
  });
  return response.data; // { totals: { email: { opens, clicks, ... } } }
};

export const markReplyAsRead = async (replyId) => {
  const response = await request(`/api/tracking/replies/${encodeURIComponent(replyId)}/read`, {
    method: 'PATCH',
    body: JSON.stringify({ userId: 'system' })
  });
  return response.data || response;
};

// Claim / release a reply. A 409 or 403 is an answer, not a failure -- its body names who holds
// the reply -- so 4xx comes back as { success:false, message, data } instead of throwing.
const claimCall = (method, replyId) =>
  axiosClientEmailMarketing({
    method,
    url: `tracking/replies/${encodeURIComponent(replyId)}/claim`,
    validateStatus: (s) => s < 500,
  }).then((r) => r.data);
export const claimReply = (replyId) => claimCall('POST', replyId);
export const releaseReply = (replyId) => claimCall('DELETE', replyId);

// The session user; its id (or e-mail) is what the server stores as claimedBy.
export const getCurrentUser = async () => (await request('/api/auth/me'))?.data?.user || null;

// (Sent emails module removed)

// --- A/B Testing API Calls ---

/**
 * Creates an A/B test for a campaign.
 * @param {string} campaignId - The ID of the campaign.
 * @param {object} abTestData - The A/B test configuration data.
 */
export const createAbTest = async (campaignId, abTestData) => {
  const response = await request(`/api/campaigns/${campaignId}/abtest`, {
    method: 'POST',
    body: JSON.stringify(abTestData),
  });
  return response.data;
};

/**
 * Gets the A/B test configuration for a campaign.
 * @param {string} campaignId - The ID of the campaign.
 */
export const getAbTest = async (campaignId) => {
  const response = await request(`/api/campaigns/${campaignId}/abtest`);
  return response.data;
};

/**
 * Updates the A/B test configuration for a campaign.
 * @param {string} campaignId - The ID of the campaign.
 * @param {object} updates - The updates to apply.
 */
export const updateAbTest = async (campaignId, updates) => {
  const response = await request(`/api/campaigns/${campaignId}/abtest`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
  return response.data;
};

/**
 * Starts an A/B test for a campaign.
 * @param {string} campaignId - The ID of the campaign.
 */
export const startAbTest = async (campaignId) => {
  const response = await request(`/api/campaigns/${campaignId}/abtest/start`, {
    method: 'POST',
  });
  return response.data;
};

/**
 * Manually evaluates an A/B test to determine the winner.
 * @param {string} campaignId - The ID of the campaign.
 */
export const evaluateAbTest = async (campaignId) => {
  const response = await request(`/api/campaigns/${campaignId}/abtest/evaluate`, {
    method: 'POST',
  });
  return response.data;
};

/**
 * Tracks an event for A/B testing.
 * @param {string} campaignId - The ID of the campaign.
 * @param {string} variant - The variant (A or B).
 * @param {string} eventType - The event type (delivered, open, click, conversion).
 * @param {number} count - The count of events (optional).
 */
export const trackAbTestEvent = async (campaignId, variant, eventType, count = 1) => {
  const response = await request(`/api/campaigns/${campaignId}/abtest/events`, {
    method: 'POST',
    body: JSON.stringify({ variant, eventType, count }),
  });
  return response.data;
};

/**
 * Gets A/B test metrics for a campaign.
 * @param {string} campaignId - The ID of the campaign.
 */
export const getAbTestMetrics = async (campaignId) => {
  const response = await request(`/api/campaigns/${campaignId}/abtest/metrics`);
  return response.data;
};

// --- Image Upload API Calls ---

/**
 * Uploads an image file to AWS S3 via CloudFront.
 * @param {File} file - The image file to upload.
 */
export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('image', file);
  
  try {
    const response = await axiosClientEmailMarketing.post('/images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || `HTTP error! status: ${error.response.status}`);
    }
    throw error;
  }
};

/**
 * Gets a presigned URL for direct upload to S3.
 * @param {string} fileName - The desired file name.
 * @param {string} mimetype - The file MIME type.
 */
export const getPresignedUrl = async (fileName, mimetype) => {
  const response = await request('/api/images/presigned-url', {
    method: 'POST',
    body: JSON.stringify({ fileName, mimetype }),
  });
  return response.data;
};

/**
 * Gets information about an uploaded image.
 * @param {string} fileName - The file name (with path).
 */
export const getImageInfo = async (fileName) => {
  const response = await request(`/api/images/info/${encodeURIComponent(fileName)}`);
  return response.data;
};

/**
 * Lists uploaded images.
 * @param {string} prefix - Optional prefix to filter images.
 * @param {number} limit - Maximum number of images to return.
 */
export const listImages = async (prefix = 'images/', limit = 1000) => {
  const params = new URLSearchParams({ prefix, limit }).toString();
  const response = await request(`/api/images/list?${params}`);
  return response.data;
};

/**
 * Deletes an image from S3.
 * @param {string} fileName - The file name (with path) to delete.
 */
export const deleteImage = async (fileName) => {
  const response = await request(`/api/images/${encodeURIComponent(fileName)}`, {
    method: 'DELETE',
  });
  return response.data;
};

/**
 * Checks the health of the image upload service.
 */
export const checkImageServiceHealth = async () => {
  const response = await request('/api/images/health');
  return response.data;
};

// --- Template Placeholders API Calls ---

/**
 * Gets all available field options for template placeholders.
 * @returns {Promise<Object>} Field options with sources, groups, and field mappings.
 */
export const fetchFieldOptions = async () => {
  const response = await request('/api/template-placeholders/field-options');
  return response.data || response;
};

/**
 * Fetches a sample record from specified source/group/id for preview.
 * @param {Object} params - { source, group, id }
 * @returns {Promise<Object>} Record data mapped to field IDs.
 */
export const fetchRecord = async (params) => {
  const response = await request('/api/template-placeholders/fetch-record', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  return response.data || response;
};

/**
 * Gets available sources and groups for filtering.
 * @returns {Promise<Object>} Available sources, groups, and their mappings.
 */
export const fetchSourcesAndGroups = async () => {
  const response = await request('/api/template-placeholders/sources-groups');
  return response.data || response;
};

/**
 * Validates a placeholder mapping configuration.
 * @param {Object} params - { mapping, requiredPlaceholders }
 * @returns {Promise<Object>} Validation result with errors and warnings.
 */
export const validateMapping = async (params) => {
  const response = await request('/api/template-placeholders/validate-mapping', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  return response.data || response;
};

/**
 * Sequential attachment processor.
 * Uploads local File objects to S3 and transforms them into standard URL references.
 * @param {Array} attachments - List of attachments from State
 * @returns {Promise<Array>} List of resolved metadata attachments
 */
export const processAttachments = async (attachments) => {
  if (!attachments || !attachments.length) return [];
  const processed = [];
  for (const att of attachments) {
    if (att.file && att.file instanceof File) {
      try {
        console.log(`Uploading file ${att.name || att.file.name}...`);
        const uploadResult = await uploadImage(att.file);
        processed.push({
          id: att.id,
          name: att.name || att.file.name,
          size: att.size || att.file.size,
          type: att.type || att.file.type,
          url: uploadResult.url,
          cloudfrontUrl: uploadResult.cloudfrontUrl,
          fileName: uploadResult.fileName,
          // Rendered server-side at upload -- page one of a PDF, or the image scaled down.
          // The e-mail's attachment chip shows this; without it the chip falls back to a
          // plain document card. Absent for Office documents, which cannot be rasterised.
          thumbnailUrl: uploadResult.thumbnailUrl,
          thumbnailFileName: uploadResult.thumbnailFileName
        });
      } catch (error) {
        console.error(`Failed to upload attachment ${att.name || 'unknown'}:`, error);
        throw error;
      }
    } else {
      processed.push(att);
    }
  }
  return processed;
};

/**
 * Distinct values for a column's filter menu, across the whole dataset rather than the page
 * the table happens to hold. Server-side and Redis-cached -- see facetService on the backend.
 * `q` asks the server to search when a column has more values than the menu will show.
 */
export const fetchColumnFacets = async (scope, column, { q = '', offset = 0, limit } = {}) => {
  const params = new URLSearchParams({ column });
  // No limit by default: the server sizes each batch from the column's distinct total, so a
  // three-value column arrives in one round trip and a huge one arrives in even slices.
  if (limit) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  if (q) params.set('q', q);
  const response = await request(`/api/${scope}/facets?${params.toString()}`);
  return response.data; // { values: [{value, count}], total, offset, limit, hasMore, truncated }
};
